// frontend/src/pages/OwnerDashboard.js
import { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { safeGetItem } from "../utils/storage";

export default function OwnerDashboard() {
  const [consultancy, setConsultancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadsCount, setLeadsCount] = useState(0);
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [clock, setClock] = useState(new Date());

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOwnerConsultancy = async () => {
      try {
        const token = safeGetItem("token");

        const res = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error(`Failed to load consultancy: ${res.status}`);
        }

        const data = await res.json();
        setConsultancy(data || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOwnerConsultancy();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!consultancy?._id) return;
      try {
        const token = safeGetItem("token");
        const res = await fetch(
          `${BACKEND_URL}/leads?consultancyId=${consultancy._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        setLeadsCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        console.error("Leads fetch error", err);
      }
    };
    fetchLeads();
  }, [consultancy?._id]);

  const offerStatus = (offer) => {
    const today = new Date();
    if (!offer.validTill) return "Active";
    const expiry = new Date(offer.validTill);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "Expired";
    if (diff > 14) return "Upcoming";
    return "Active";
  };

  const parseDiscountValue = (offer) => {
    if (!offer.discount) return 0;
    const numeric = parseFloat(offer.discount.toString().replace(/[^\d.]/g, ""));
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const offersWithMeta = useMemo(() => {
    const source = consultancy?.offers || [];
    return source.map((offer) => ({
      ...offer,
      status: offerStatus(offer),
      discountValue: parseDiscountValue(offer),
    }));
  }, [consultancy?.offers]);

  if (loading)
    return <div className="p-6 text-center text-gray-600">Loading dashboard...</div>;

  if (!consultancy)
    return <div className="p-6 text-center text-red-500">No consultancy found</div>;

  const handleEditOffer = (offerId) => {
    navigate(`/owner/edit-offer/${offerId}`);
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm("Delete this offer?")) return;

    try {
      const token = safeGetItem("token");
      const res = await fetch(
        `${BACKEND_URL}/consultancies/${consultancy._id}/offers/${offerId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        alert("Failed to delete offer");
        return;
      }

      setConsultancy((prev) =>
        prev
          ? {
              ...prev,
              offers: prev.offers.filter((offer) => offer._id !== offerId),
            }
          : prev
      );
    } catch (err) {
      console.error("Delete offer error", err);
      alert("Error deleting offer");
    }
  };

  const handleDuplicateOffer = async (offer) => {
    try {
      const token = safeGetItem("token");
      const res = await fetch(
        `${BACKEND_URL}/consultancies/${consultancy._id}/offers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: `${offer.title || "Offer"} (Copy)`,
            description: offer.description || "",
            discount: offer.discount || "",
            validTill: offer.validTill || null,
          }),
        }
      );

      if (!res.ok) {
        alert("Failed to duplicate offer");
        return;
      }

      const updated = await res.json();
      setConsultancy(updated);
    } catch (err) {
      console.error("Duplicate offer error", err);
      alert("Error duplicating offer");
    }
  };

  const filters = ["All", "Active", "Expired", "Upcoming", "Highest Discount"];
  const sortOptions = ["Newest", "Oldest", "Highest Discount"];

  const filteredOffers = offersWithMeta.filter((offer) => {
    switch (filter) {
      case "Active":
        return offer.status === "Active";
      case "Expired":
        return offer.status === "Expired";
      case "Upcoming":
        return offer.status === "Upcoming";
      case "Highest Discount":
        const highest = Math.max(...offersWithMeta.map((o) => o.discountValue));
        return offer.discountValue === highest && highest > 0;
      default:
        return true;
    }
  });

  const createdValue = (offer) =>
    new Date(offer.createdAt || offer.updatedAt || offer.validTill || Date.now()).getTime();

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    if (sortBy === "Newest") {
      return createdValue(b) - createdValue(a);
    }
    if (sortBy === "Oldest") {
      return createdValue(a) - createdValue(b);
    }
    if (sortBy === "Highest Discount") {
      return b.discountValue - a.discountValue;
    }
    return 0;
  });

  const totalOffers = consultancy.offers?.length || 0;
  const activeOfferCount = offersWithMeta.filter((o) => o.status === "Active").length;
  const profileViews =
    consultancy.profileViews ??
    consultancy.profile_views ??
    consultancy.viewCount ??
    0;
  const subscriptionStatusDescription =
    "All premium capabilities are unlocked while AbroadJacks runs its open beta.";

  const primaryStats = [
    {
      label: "TOTAL OFFERS",
      value: totalOffers,
      icon: "🎯",
      gradient: "from-indigo-500 via-indigo-600 to-purple-500",
    },
    {
      label: "TOTAL LEADS",
      value: leadsCount,
      icon: "👥",
      gradient: "from-sky-500 via-blue-500 to-indigo-500",
    },
    {
      label: "PROFILE VIEWS",
      value: profileViews,
      icon: "👀",
      gradient: "from-amber-500 via-orange-500 to-rose-500",
    },
  ];

  const secondaryStats = [
    {
      label: "ACTIVE OFFERS",
      value: activeOfferCount,
      icon: "🔥",
    },
    {
      label: "BETA ACCESS",
      value: "All features unlocked",
      icon: "🆓",
    },
    {
      label: "SUBSCRIPTION PLANS",
      value: "Launching soon",
      icon: "🚀",
    },
  ];

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-GB") : "No expiry";

  const statusStyles = {
    Active: "text-emerald-400 bg-emerald-500/10 border border-emerald-400/30",
    Expired: "text-red-400 bg-red-500/10 border border-red-400/30",
    Upcoming: "text-blue-300 bg-blue-500/10 border border-blue-400/30",
  };

  const actionCards = [
    {
      title: "Leads Panel",
      description: "View & manage every incoming inquiry in one place.",
      cta: "Open Leads",
      onClick: () => navigate("/owner/leads"),
      icon: "📨",
    },
    {
      title: "Settings Panel",
      description: "Edit logo, profile, countries, gallery & more.",
      cta: "Open Settings",
      onClick: () => navigate("/owner/settings"),
      icon: "⚙️",
    },
    {
      title: "Public View Panel",
      description: "Preview exactly how students experience your brand.",
      cta: "Preview Public Page",
      onClick: () => navigate(`/consultancy/${consultancy._id}`),
      icon: "🌐",
    },
  ];

  const formattedClock = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(clock);

  return (
    <div className="space-y-8 text-white">
      <div className="rounded-3xl bg-gradient-to-r from-indigo-900 via-blue-900 to-slate-900 p-8 shadow-[0_25px_70px_rgba(15,23,42,0.45)] flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[6px] text-white/60">
            Owner Control Center
          </p>
          <h2 className="mt-2 text-3xl font-semibold">
            Welcome back, {consultancy.name}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="px-4 py-1 rounded-full text-xs font-semibold tracking-[4px] uppercase bg-white/10 border border-white/20">
              Free Beta · All features unlocked
            </span>
            <span className="text-white/70 text-sm">{consultancy.email}</span>
          </div>
          <p className="mt-3 text-white/70 text-sm max-w-2xl">
            {subscriptionStatusDescription}
          </p>
        </div>
        <div className="text-right">
          <p className="text-white/50 text-sm">Today</p>
          <p className="text-2xl font-semibold">{formattedClock}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {primaryStats.map((stat) => (
          <div
            key={stat.label}
            className={`rounded-3xl p-5 bg-gradient-to-br ${stat.gradient} shadow-lg hover:shadow-xl transition transform hover:-translate-y-1`}
          >
            <div className="text-3xl">{stat.icon}</div>
            <p className="mt-4 text-4xl font-bold">{stat.value}</p>
            <p className="text-sm uppercase tracking-[4px] text-white/70 mt-1">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {secondaryStats.map((stat) => (
          <div
            key={stat.label}
            className="rounded-3xl border border-white/10 bg-white/5 p-5 flex items-center gap-4 shadow-[0_20px_50px_rgba(15,23,42,0.35)]"
          >
            <div className="text-3xl">{stat.icon}</div>
            <div>
              <p className="text-xs uppercase tracking-[4px] text-white/60">
                {stat.label}
              </p>
              <p className="text-2xl font-semibold mt-1">{stat.value ?? "—"}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-6 space-y-6 shadow-[0_35px_60px_rgba(15,23,42,0.35)]">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-2xl font-semibold">Offer Management</h3>
            <p className="text-white/70 text-sm">
              Monitor, sort, and launch high-performing campaigns.
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex flex-wrap gap-2">
              {filters.map((btn) => (
                <button
                  key={btn}
                  onClick={() => setFilter(btn)}
                  className={`px-3 py-2 rounded-full text-xs font-semibold uppercase tracking-wide border ${
                    filter === btn
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-white/0 shadow-lg"
                      : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                  }`}
                >
                  {btn}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-white/70 uppercase tracking-[4px]">
                Sort by
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-2xl px-3 py-2 text-sm focus:outline-none"
              >
                {sortOptions.map((opt) => (
                  <option key={opt} value={opt} className="text-slate-800">
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => navigate("/owner/add-offer")}
              className="px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-blue-500 text-white font-semibold shadow-lg hover:opacity-95"
            >
              + Create New Offer
            </button>
          </div>
        </div>

        {sortedOffers.length === 0 ? (
          <p className="text-white/70 italic">No offers match the selected filters.</p>
        ) : (
          <div className="space-y-4">
            {sortedOffers.map((offer) => (
              <div
                key={offer._id}
                className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-xl font-semibold capitalize">{offer.title}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs ${statusStyles[offer.status]}`}>
                      {offer.status}
                    </span>
                  </div>
                  <p className="text-white/70 mt-2">{offer.description || "No description provided."}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-white/70">
                    <span className="flex items-center gap-1">
                      <span className="text-white">💸</span> {offer.discount || "No discount"}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-white">⏳</span> {formatDate(offer.validTill)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleEditOffer(offer._id)}
                    className="px-4 py-2 rounded-2xl bg-white/10 border border-white/20 hover:bg-white/20 transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDuplicateOffer(offer)}
                    className="px-4 py-2 rounded-2xl bg-white/10 border border-white/20 hover:bg-white/20 transition"
                  >
                    📄 Duplicate
                  </button>
                  <button
                    onClick={() => handleDeleteOffer(offer._id)}
                    className="px-4 py-2 rounded-2xl bg-red-500/10 border border-red-400/30 text-red-200 hover:bg-red-500/20 transition"
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {actionCards.map((card) => (
          <div
            key={card.title}
            className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_25px_60px_rgba(15,23,42,0.35)] hover:shadow-[0_35px_70px_rgba(59,130,246,0.35)] transition"
          >
            <div className="text-4xl">{card.icon}</div>
            <h4 className="mt-4 text-xl font-semibold">{card.title}</h4>
            <p className="text-white/70 text-sm mt-2">{card.description}</p>
            <button
              onClick={card.onClick}
              className="mt-5 w-full rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 py-3 font-semibold hover:opacity-95"
            >
              {card.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
